import * as Sentry from '@sentry/react';
import { useEffect, useCallback } from 'react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Release version for tracking which deploy caused errors
const RELEASE_VERSION = import.meta.env.VITE_COMMIT_REF 
  || import.meta.env.VITE_APP_VERSION 
  || 'unknown';

export const initSentry = () => {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    ignoreErrors: [
      // Safari/iOS WebKit noise — browser extensions or in-app browsers calling native handlers
      "undefined is not an object (evaluating 'window.webkit.messageHandlers",
      // Common browser extension noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Netlify RUM script blocked by ad blockers / privacy tools — not our code
      /Load failed \(ingesteer\.services-prod\.nsvcs\.net\)/,
      /ingesteer\.services-prod\.nsvcs\.net/,
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        // Track API calls automatically
        // tracePropagationTargets configured at Sentry.init level if needed
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true, // Protect sensitive inputs
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0, // 20% in production, 100% in dev
    // Session Replay
    replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0.1, // 5% sessions in prod
    replaysOnErrorSampleRate: 1.0, // 100% of errors
    // Environment
    environment: import.meta.env.MODE,
    release: RELEASE_VERSION,
    // Only enable in production unless explicitly enabled in dev
    enabled: import.meta.env.PROD || import.meta.env.VITE_ENABLE_SENTRY_DEV === 'true',
    // Privacy: scrub sensitive data
    beforeSend(event) {
      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
      }
      // Scrub potential PII from extra data
      if (event.extra?.user) {
        const { email, phone, ...safeUser } = event.extra.user as Record<string, unknown>;
        event.extra.user = safeUser;
      }
      return event;
    },
    // Group similar errors
    beforeBreadcrumb(breadcrumb) {
      // Filter out noisy console logs in production
      if (breadcrumb.category === 'console' && import.meta.env.PROD) {
        // Only keep errors and warnings
        if (breadcrumb.level !== 'error' && breadcrumb.level !== 'warning') {
          return null;
        }
      }
      return breadcrumb;
    },
  });
};

// Performance tracking helpers
export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpan({ name, op }, () => {});
};

export const startSpan = (name: string, fn: () => void) => {
  return Sentry.startSpan({ name }, fn);
};

// Breadcrumb helpers
export const addBreadcrumb = (message: string, category?: string, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category: category || 'action',
    data,
    level: 'info',
  });
};

export const addNavigationBreadcrumb = (from: string, to: string) => {
  Sentry.addBreadcrumb({
    message: `Navigation: ${from} → ${to}`,
    category: 'navigation',
    data: { from, to },
    level: 'info',
  });
};

export const addAPICallBreadcrumb = (endpoint: string, method: string, success: boolean, duration?: number) => {
  Sentry.addBreadcrumb({
    message: `${method} ${endpoint} ${success ? 'success' : 'failed'}`,
    category: 'api',
    data: { endpoint, method, success, duration },
    level: success ? 'info' : 'warning',
  });
};

// Error capture with context
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  Sentry.captureException(error, { extra: context });
};

export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') => {
  Sentry.captureMessage(message, level);
};

// User context
export const setUser = (user: { id: string; email?: string; subscriptionTier?: string }) => {
  Sentry.setUser({ id: user.id, subscriptionTier: user.subscriptionTier });
};

export const clearUser = () => {
  Sentry.setUser(null);
};

// React hook for Sentry
export const useSentry = () => {
  const addActionBreadcrumb = useCallback((action: string, data?: Record<string, unknown>) => {
    addBreadcrumb(action, 'user_action', data);
  }, []);

  const captureError = useCallback((error: Error, context?: Record<string, unknown>) => {
    captureException(error, context);
  }, []);

  return {
    addBreadcrumb: addActionBreadcrumb,
    captureError,
    setUser,
    clearUser,
  };
};

// Hook to set user from auth
export const useSentryUser = (userId?: string, email?: string, subscriptionTier?: string) => {
  useEffect(() => {
    if (userId) {
      setUser({ id: userId, email, subscriptionTier });
    } else {
      clearUser();
    }
    
    return () => {
      clearUser();
    };
  }, [userId, email, subscriptionTier]);
};

// Error Boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Show report dialog
export const showReportDialog = (eventId?: string) => {
  Sentry.showReportDialog({ eventId });
};

// Unified error logger: console.error + Sentry capture with context
// Use this instead of bare console.error in hooks and components
export const logError = (
  message: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  console.error(message, error);

  const err = error instanceof Error
    ? error
    : new Error(typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message: unknown }).message)
        : String(error));

  Sentry.captureException(err, {
    extra: { message, ...context },
  });
};

// Capture Supabase query errors (non-exception objects with message/code)
export const logSupabaseError = (
  operation: string,
  error: { message: string; code?: string; details?: string },
  context?: Record<string, unknown>
) => {
  console.error(`${operation}:`, error);

  Sentry.captureMessage(`Supabase: ${operation}`, {
    level: 'error',
    extra: {
      errorMessage: error.message,
      errorCode: error.code,
      errorDetails: error.details,
      ...context,
    },
  });
};

// Track auth events (login, register, password reset)
export const trackAuthEvent = (
  event: 'login_success' | 'login_failure' | 'register_success' | 'register_failure' | 'logout' | 'password_reset',
  metadata?: Record<string, unknown>
) => {
  addBreadcrumb(`Auth: ${event}`, 'auth', metadata);

  // Login failures are normal user behavior — breadcrumb only.
  // Register failures may indicate a real problem, keep as Sentry issue.
  if (event === 'register_failure') {
    Sentry.captureMessage(`Auth: ${event}`, {
      level: 'warning',
      extra: metadata,
    });
  }
};

// Track content generation lifecycle
export const trackGeneration = (
  phase: 'started' | 'polling' | 'completed' | 'timeout' | 'error',
  metadata?: Record<string, unknown>
) => {
  const level = phase === 'error' || phase === 'timeout' ? 'warning' : 'info';
  addBreadcrumb(`Generation: ${phase}`, 'content_generation', metadata);

  if (phase === 'timeout' || phase === 'error') {
    Sentry.captureMessage(`Generation: ${phase}`, {
      level,
      extra: metadata,
    });
  }
};

// Log edge function errors with Polish user-facing message
// Returns a Polish error message suitable for toast display
export const logEdgeFunctionError = (
  functionName: string,
  error: unknown,
  context?: Record<string, unknown>
): string => {
  const rawMessage = error instanceof Error
    ? error.message
    : typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);

  console.error(`Edge Function "${functionName}" error:`, error);

  const err = error instanceof Error ? error : new Error(rawMessage);

  Sentry.captureException(err, {
    extra: {
      edgeFunction: functionName,
      rawMessage,
      ...context,
    },
    tags: {
      error_source: 'edge_function',
      edge_function: functionName,
    },
  });

  return 'Nie udało się połączyć z serwerem. Spróbuj ponownie.';
};

// Track feature usage
export const trackFeatureUsage = (featureName: string, metadata?: Record<string, unknown>) => {
  addBreadcrumb(`Feature used: ${featureName}`, 'feature_usage', metadata);
  
  // Metric tracking via breadcrumbs only
};
